/**
 * File: useNumbersQueries.ts
 * Path: apps/admin/src/shared/hooks/useNumbersQueries.ts
 * Description: Loads everything the numbers add up (leads, clients, contracts
 * and their payments, time and costs, retainers). The Today page reads it.
 */

import { useMemo, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { mergeRetainers, southAfricanDay, FIRESTORE_COLLECTIONS } from "@idk/config";
import { monthsOf } from "../utils/numbersPeriodUtils";
import { type NumbersInput } from "../utils/numbersUtils";
import { useClientsList } from "@/shared/hooks/useClientsQueries";
import { useAllContractPayments, useContractsList } from "@/shared/hooks/useContractsQueries";
import { effortRepo } from "@/shared/services/effortService";
import { leadsRepo } from "@/shared/services/leadsService";
import { useLeadsList } from "@/shared/hooks/useLeadsQueries";
import { useStoredRetainers } from "@/shared/hooks/useRetainersQueries";

export interface NumbersInputResult {
  /** Null while anything is still loading. */
  input: NumbersInput | null;
  failed: boolean;
}

/**
 * Loads every record the numbers add up (leads, clients, contracts and their
 * payments, time and costs, retainers). Time, costs and monthly costs are read
 * from `since` (a YYYY-MM-DD day) onwards, and calls and emails are counted
 * from `outreachFrom`. The Numbers page and the Today page both read it.
 */
export const useNumbersInput = (since: string, outreachFrom: string): NumbersInputResult => {
  const [now] = useState(() => Date.now());
  const today = useMemo(() => southAfricanDay(now), [now]);

  const leads = useLeadsList();
  const clients = useClientsList();
  const contracts = useContractsList();
  const stored = useStoredRetainers();
  const signed = (contracts.data ?? []).filter((c) => c.status === "client_signed" || c.status === "signed");
  const payments = useAllContractPayments();
  const time = useQuery({
    queryKey: [FIRESTORE_COLLECTIONS.timeEntries, "since", since] as const,
    queryFn: () => effortRepo.timeSince(since),
  });
  const costs = useQuery({
    queryKey: [FIRESTORE_COLLECTIONS.costEntries, "since", since] as const,
    queryFn: () => effortRepo.costsSince(since),
  });
  const months = useMemo(() => monthsOf({ from: since, to: today }), [since, today]);
  const monthly = useQueries({
    queries: months.map((m) => ({ queryKey: [FIRESTORE_COLLECTIONS.crmCosts, m] as const, queryFn: () => effortRepo.monthly(m) })),
  });
  const startMs = Date.parse(`${outreachFrom}T00:00:00+02:00`);
  const outreach = useQuery({
    queryKey: [FIRESTORE_COLLECTIONS.leads, "outreach", startMs] as const,
    queryFn: () => leadsRepo.outreachSince(startMs),
  });

  const all = [leads, clients, contracts, stored, time, costs, outreach, payments, ...monthly];
  const failed = all.some((q) => q.isError && q !== outreach);
  const ready = all.every((q) => !q.isLoading) && !failed && Boolean(leads.data && clients.data && contracts.data && stored.data && time.data && costs.data && payments.data);

  // Stamps stand in for the query arrays, which are new objects every render.
  const monthlyStamp = monthly.map((r) => r.dataUpdatedAt).join();

  return useMemo(() => {
    if (!ready) return { input: null, failed };
    const byContract = <T,>(all: Record<string, T[]> | undefined) =>
      Object.fromEntries(signed.map((c) => [c.id, all?.[c.id] ?? []])) as Record<string, T[]>;
    const input: NumbersInput = {
      today,
      now,
      leads: leads.data ?? [],
      clients: clients.data ?? [],
      contracts: contracts.data ?? [],
      receipts: byContract(payments.data?.receipts),
      dueDates: byContract(payments.data?.dueDates),
      time: time.data ?? [],
      costs: costs.data ?? [],
      monthly: monthly.flatMap((m) => (m.data ? [m.data] : [])),
      retainers: mergeRetainers(contracts.data ?? [], stored.data ?? []),
      outreach: outreach.data ?? null,
    };
    return { input, failed };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, failed, today, now, leads.data, clients.data, contracts.data, stored.data, time.data, costs.data, outreach.data, payments.data, monthlyStamp]);
};
